/**
 * Salon Desk feature flags — simple checkout vs full Salon ERP POS.
 * See docs/SALON-DESK-VISION.md and DEVELOPMENT-PLAN.md Phase 3.
 */
export const SALON_DESK_CHECKOUT = {
  /** Master switch for simplified checkout UI */
  lite: true,
  showShortcutBar: false,
  showGenderToggle: false,
  showRetailAndQuickKeys: true,
  showPackageModals: true,
  showKeyboardShortcuts: false,
  showFloorQueue: false,
  showPackageBalances: true,
  showOperationsPanel: false,
  showTips: false,
  showPromoCodes: true,
  showGateway: false,
  /** Loyalty earn/redeem when a guest is on the ticket */
  showLoyaltyRedemption: true,
  /** Subtotal, VAT, discount, and total on cart + payment panel */
  showTotalsBreakdown: true,
  showTaxInvoice: true,
  showServiceSlips: false,
  allowSplitPayment: true,
  showAddons: false,
  /** Share receipt link via WhatsApp after pay */
  showWhatsAppReceiptShare: true,
  /** Single price — backend uses price_ladies as the one price column */
  fixedPricingGender: 'ladies',
  allowedPaymentCodes: ['cash', 'card', 'transfer', 'apple_pay', 'other'],
  /** Guest name + phone required before hold or payment */
  requireGuestForCheckout: true,
}

/**
 * @param {import('@/types/api').PaymentMethod[]} methods
 */
export function filterCheckoutPaymentMethods(methods) {
  if (!SALON_DESK_CHECKOUT.lite) {
    return methods
  }

  const allowed = new Set(SALON_DESK_CHECKOUT.allowedPaymentCodes)
  const filtered = methods.filter((method) => allowed.has(method.code))

  return filtered.length > 0 ? filtered : methods
}

/**
 * Salon Desk calendar / appointments — see DEVELOPMENT-PLAN.md Phase 4.
 */
export const SALON_DESK_APPOINTMENTS = {
  lite: true,
  defaultOpsView: 'schedule',
  showFloorTab: false,
  showBlocksTab: false,
  showWeekView: true,
  showMonthView: true,
  showWalkinQueue: true,
  showDragReschedule: false,
  showAppointmentLookup: true,
  plainStatusLabels: true,
  /**
   * When true (set VITE_CALENDAR_DEMO_DATA=true), use realistic demo bookings
   * for visual QA. Never enable in production builds by default.
   */
  useDemoSchedule: import.meta.env.VITE_CALENDAR_DEMO_DATA === 'true',
}

/**
 * Salon Desk online booking inbox — see DEVELOPMENT-PLAN.md Phase 4.
 */
export const SALON_DESK_ONLINE_BOOKING = {
  lite: true,
  showBrandingPanel: false,
  showGatewayPanel: false,
  showShareAdminTab: false,
  showConfirmationModeKpi: false,
}

/**
 * Salon Desk masters — Customers, Services, Staff (Phase 5).
 */
export const SALON_DESK_MASTERS = {
  lite: true,
  /** Customers */
  showCustomerPackagesTab: true,
  showCustomerEmail: true,
  showCustomerDob: true,
  showCustomerAllergies: true,
  showCustomerPreferredStylist: true,
  showCustomerMarketingConsent: true,
  showCustomerBlacklist: false,
  showCustomerLoyaltyPoints: true,
  /** Services — categories merged into Services page */
  mergeCategoriesIntoServicesPage: true,
  singleServicePrice: true,
  showServiceArabicName: false,
  showDualPricing: false,
  showServiceSkillLevel: true,
  showServiceImage: false,
  showServiceConsumables: true,
  showServiceDuplicate: false,
  /** Simple staff commission % per service (Salon Desk) */
  showServiceCommission: true,
  showCategoryGenderScope: false,
  showCategoryColour: false,
  /** Staff */
  staffPageTitle: 'Staff',
  showCommissionProfiles: true,
  showStylistSkills: true,
  showStylistWps: false,
  showStylistIban: false,
  /** Owner: POS ticket history per staff member */
  showStaffPosHistory: true,
  showStaffAttendance: true,
  showBeautyProfile: true,
  showCustomerPhotos: true,
  showCustomerRecommendations: true,
}

/**
 * @param {import('@/types/api').Service | { price_ladies?: number | string | null, price_gents?: number | string | null }} service
 */
export function getServiceDisplayPrice(service) {
  const ladies = Number(service?.price_ladies)
  if (Number.isFinite(ladies) && ladies > 0) {
    return ladies
  }

  const gents = Number(service?.price_gents)
  return Number.isFinite(gents) ? gents : 0
}

/**
 * @param {number | string | null | undefined} price
 * @param {number | string | null | undefined} percent
 */
export function getServiceCommissionAmount(price, percent) {
  const amount = Number(price)
  const rate = Number(percent)
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(rate) || rate <= 0) {
    return 0
  }

  return Math.round(amount * (rate / 100) * 100) / 100
}

/**
 * Salon Desk is single-store only. Keep branch_id / X-Branch-Id plumbing,
 * but never expose multi-location switching or create/compare flows.
 */
export const SALON_DESK_SINGLE_STORE = true

/**
 * Salon Desk sales + settings — see DEVELOPMENT-PLAN.md Phase 6.
 */
export const SALON_DESK_SALES = {
  lite: true,
  showWeekTable: true,
  showPaymentBreakdown: true,
  showCsvExport: true,
  simplifiedWeekColumns: true,
  showAllLocationsTab: false,
  /** Owner-only tabs on Sales page */
  showAdminReports: true,
}

export const SALON_DESK_SETTINGS = {
  lite: true,
  inlineSections: true,
  showOnlineBookingLink: true,
  showBookingMessagesInSettings: true,
  showLoyaltySettings: true,
  showSendOffersPage: true,
  showAdvancedSettingsSections: false,
  showLegacyAdminLinks: false,
  showAuditLog: true,
  showRemindersSection: true,
  showAccountsSection: true,
}

/** Phase 8 — UI polish flags */
export const SALON_DESK_UI = {
  showOnboardingWizard: true,
  plainEnglishErrors: true,
  largerCheckoutType: true,
  largerAppointmentsType: true,
}

/** Today home — plain diary view, no ERP floor/waitroom metrics */
export const SALON_DESK_TODAY = {
  lite: true,
  showSalonPulse: false,
  showWaitroomCountdowns: false,
  showWeekBarChart: false,
}
